import { ChangeEvent, FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import styles from './addStaffDialog.module.scss'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Email, QrCodeScanner } from '@mui/icons-material'
import { useMutation } from '@apollo/client'
import UserInput from '@/components/_custom/UserInput/userInput'
import Tabs from '@/components/_custom/Tabs/tabs'
import { GET_STAFF_AUTO_LINK_MAIL } from '@/modules/API/software/staffAutoLinkHandler'
import ShowQRCodeDialog from '../../../families/list/header/Dialog/showQRCodeDialog'
import { CREATE_MOCK_STAFF, STAFF_LINKS } from '@/modules/API/daycare/staffLinkHandler'
import { useAuth } from '@/modules/context/AuthContext'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { Diplomas } from '@/types/Staff.ts'
import { getDiplomaSelectOptions } from '../../../account/profile/staffProfile/staffProfile.tsx'
import { SelectOptionsDeprecated } from '@/types/Select.ts'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique.tsx'
import { Daycare } from '@/types/Daycare.ts'

interface AddStaffDialogProps {
    open: boolean
    closeDialog: () => void
    daycareId: number
    autoLinkActive: boolean
    autoLinkDaycare: string
    refetchAllStaffList?: () => Promise<void>
}

const AddStaffDialog: FunctionComponent<AddStaffDialogProps> = ({
    open,
    closeDialog,
    daycareId,
    autoLinkActive,
    autoLinkDaycare,
    refetchAllStaffList
}: AddStaffDialogProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [tabSwitch, setTabSwitch] = useState<number>(0)
    const [mailSent, setMailSent] = useState<boolean>(false)

    const [email, setEmail] = useState<string>('')
    const [validEmail, setValidEmail] = useState<boolean>(false)

    const [firstname, setFirstname] = useState<string>('')
    const [lastname, setLastname] = useState<string>('')
    const [diploma, setDiploma] = useState<Diplomas>(Diplomas.no)

    const diplomaSelectOptions: SelectOptionsDeprecated = getDiplomaSelectOptions(t)

    const [isQRCodeDialogOpen, setQRCodeDialogOpen] = useState<boolean>(false)

    const [sendAutoLinkMail] = useMutation(GET_STAFF_AUTO_LINK_MAIL, {
        variables: {
            daycareId: Number(daycareId)
        },
        onCompleted: () => {
            setMailSent(true)

            showNotificationSuccess(t('autoLinkMailSuccess'))
        },
        onError: () => {
            showNotificationError(t('autoLinkMailError'))
        }
    })

    const [addStaff, { loading: loadingAddStaff }] = useMutation(STAFF_LINKS, {
        variables: {
            daycare_id: Number(daycareId),
            email: email
        },
        onCompleted: () => {
            showNotificationSuccess(t('addStaffSuccess'))

            if (refetchAllStaffList) refetchAllStaffList()
            closeDialog()
        },
        onError: (error) => {
            showNotificationError(
                error.message.split('Message: ')[0] === 'No user found' ? t('userNotFound') : t('addStaffError')
            )
        }
    })

    const [createStaff, { loading: loadingCreateStaff }] = useMutation(CREATE_MOCK_STAFF, {
        variables: {
            daycareId: Number(daycareId),
            firstname,
            lastname,
            degree: diploma
        },
        onCompleted: () => {
            showNotificationSuccess(t('createStaffSuccess'))

            if (refetchAllStaffList) refetchAllStaffList()
            closeDialog()
        },
        onError: () => {
            showNotificationError(t('createStaffError'))
        }
    })

    function handleSwitch(_e: ChangeEvent<NonNullable<unknown>>, newValue: number): void {
        setTabSwitch(newValue)
    }

    function updateDiploma(diploma: string) {
        setDiploma(diploma as Diplomas)
    }

    const daycare: Daycare | undefined = user?.currentDaycare

    return (
        <Dialog open={open} handleClose={closeDialog}>
            <DialogHeader
                title={tabSwitch === 0 ? t('inviteStaff') : tabSwitch === 1 ? t('addStaff') : t('createStaff')}
            />
            <DialogBody className={styles.DialogBody}>
                {user?.currentDaycareRights.canWriteStaffRights && (
                    <Tabs value={tabSwitch} onChange={handleSwitch} tabList={[t('invite'), t('add'), t('create')]} />
                )}
                {tabSwitch === 0 ? (
                    // INVITE STAFF
                    <div className={styles.InviteContainer}>
                        {!mailSent && !autoLinkActive && (
                            <p className={styles.Text}>
                                {t('autoLinkNotActivated')}
                                <a
                                    onClick={() => navigate(`/daycare?daycare=${daycareId}#autolink`)}
                                    className={styles.Clickable}
                                >
                                    {t('clickHere')}
                                </a>
                            </p>
                        )}
                        {mailSent && <p className={styles.Text}>{t('autoLinkMailReceived')}</p>}
                        <div className={styles.ButtonInviteContainer}>
                            <ButtonTextIcon type={'close'} onClick={closeDialog} className={styles.Button} />
                            <ButtonTextIcon
                                type={'custom'}
                                onClick={() => setQRCodeDialogOpen(true)}
                                customText={t('showQRCode')}
                                disabled={!autoLinkActive}
                                customIcon={<QrCodeScanner />}
                                className={styles.Button}
                            />
                            {user?.currentDaycareRights.canReadParamsDaycare && (
                                <ButtonTextIcon
                                    type={'custom'}
                                    onClick={sendAutoLinkMail}
                                    customText={t('receiveMail')}
                                    disabled={!autoLinkActive || mailSent}
                                    customIcon={<Email />}
                                    className={styles.Button}
                                />
                            )}
                        </div>
                        <ShowQRCodeDialog
                            open={isQRCodeDialogOpen}
                            onClose={() => setQRCodeDialogOpen(false)}
                            autoLink={autoLinkDaycare}
                            pdfVariant='staff'
                            daycareName={daycare?.name}
                            daycareLogoBase64Data={daycare?.logoBase64Data}
                        />
                    </div>
                ) : tabSwitch === 1 ? (
                    // ADD STAFF
                    <div className={styles.InviteContainer}>
                        <div className={styles.Text}>
                            {t('RgpdInviteFamily')}
                            <a onClick={(e) => handleSwitch(e, 0)} className={styles.Clickable}>
                                {t('clickHere')}
                            </a>
                        </div>
                        <div className={styles.InputContainer}>
                            <UserInput
                                label={t('email')}
                                value={email}
                                setValue={setEmail}
                                fullWidth
                                verify={'email'}
                                setValid={setValidEmail}
                                className={styles.Input}
                                type={'text'}
                                unallowedCharacters={'email'}
                            />
                        </div>
                        <div className={styles.ButtonAddContainer}>
                            <ButtonTextIcon type={'close'} onClick={closeDialog} className={styles.Button} />
                            <ButtonTextIcon
                                type={'add'}
                                onClick={addStaff}
                                className={styles.Button}
                                disabled={!validEmail || loadingAddStaff}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={styles.InviteContainer}>
                        <div className={styles.Text}>
                            {`${t('NoAccount1Staff')} ${t('RgpdInviteFamily')}`}
                            <a onClick={(e) => handleSwitch(e, 0)} className={styles.Clickable}>
                                {t('clickHere')}
                            </a>
                        </div>
                        <div className={styles.InputContainer}>
                            <UserInput
                                label={t('lastname')}
                                value={lastname}
                                setValue={setLastname}
                                type='text'
                                required
                                fullWidth
                                format={'upper'}
                                className={styles.Input}
                                unallowedCharacters={'num'}
                            />
                        </div>
                        <div className={styles.InputContainer}>
                            <UserInput
                                label={t('firstname')}
                                value={firstname}
                                setValue={setFirstname}
                                type='text'
                                required
                                fullWidth
                                format={'pascal'}
                                className={styles.Input}
                                unallowedCharacters={'num'}
                            />
                        </div>
                        <div className={styles.InputContainer}>
                            <SelectUnique
                                className={styles.Input}
                                selectValue={diploma}
                                setSelectValue={updateDiploma}
                                label={t('diploma')}
                                selectOptions={diplomaSelectOptions}
                                fullWidth
                            />
                        </div>
                        <div className={styles.ButtonAddContainer}>
                            <ButtonTextIcon type={'close'} onClick={closeDialog} className={styles.Button} />
                            <ButtonTextIcon
                                type={'create'}
                                onClick={createStaff}
                                className={styles.Button}
                                disabled={firstname === '' || lastname === '' || loadingCreateStaff}
                            />
                        </div>
                    </div>
                )}
            </DialogBody>
        </Dialog>
    )
}

export default AddStaffDialog
